import React, { useState } from 'react';
import { Paper, Box, Typography, Button, TextField, CircularProgress, Alert } from '@mui/material';
import {
  ViewAgendaRounded, CheckCircleRounded, ScheduleRounded, PersonRounded, TimerRounded, VisibilityRounded,
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import { DetailHead, FilterBar, Field, SectionCard, Grid } from '../../components/common';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import DataTable, { Column } from '../../components/DataTable';
import { Avatar } from '../../components/NameCell';
import { LineChart, DoughnutChart, ChartLegend } from '../../components/Charts';
import { useProviderDashboard } from '../../hooks/useReports';
import { colors } from '../../theme/theme';

const ProviderDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  // Range applies only when both bounds are set; otherwise the dashboard is all-time.
  const ranged = !!(from && to);
  const { data, loading, error, reload } = useProviderDashboard(id, ranged ? from : undefined, ranged ? to : undefined);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 400, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'No data.'}</Alert>;
  }

  const { business, kpis, overview, trend_labels, trend, next_appointments } = data;
  const ov: Array<[string, string, number]> = [
    ['Waiting', '#60A5FA', overview.waiting],
    ['In Progress', '#F59E0B', overview.in_progress],
    ['Completed', '#10B981', overview.completed],
    ['Skipped', '#FBBF24', overview.skipped],
    ['Cancelled', '#EF4444', overview.cancelled],
  ];
  const hasOverview = ov.some((o) => o[2] > 0);

  const apptCols: Column<any>[] = [
    { key: 'time', label: 'Time' },
    { key: 'patient', label: 'Customer', render: (r) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}><Avatar name={r.patient} /><Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{r.patient}</Typography></Box> },
    { key: 'token', label: 'Token', render: (r) => <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: colors.textTertiary }}>{r.token}</Typography> },
    { key: 'status', label: 'Status', render: () => <StatusChip status="upcoming" label="Upcoming" /> },
  ];

  return (
    <>
      <DetailHead
        title={<>Welcome back, {business.owner} 👋</>}
        sub={`Operational summary for ${business.name} · ${ranged ? `${from} → ${to}` : 'All time'}.`}
        actions={
          <>
            <Button component={Link} to={id ? `/businesses/${id}` : '/businesses'} size="small" variant="outlined" startIcon={<VisibilityRounded />} sx={{ borderColor: 'divider', color: 'text.primary' }}>
              View Business details
            </Button>
            <StatusChip status="info" label={business.name} />
          </>
        }
      />

      <FilterBar>
        <Field label="From"><TextField type="date" value={from} onChange={(e) => setFrom(e.target.value)} size="small" inputProps={{ max: to || undefined }} /></Field>
        <Field label="To"><TextField type="date" value={to} onChange={(e) => setTo(e.target.value)} size="small" inputProps={{ min: from || undefined }} /></Field>
        <Field label="&nbsp;"><Button variant="outlined" size="small" onClick={() => { setFrom(''); setTo(''); }} disabled={!from && !to} sx={{ height: 40 }}>All time</Button></Field>
      </FilterBar>

      <Grid cols={5} sx={{ mb: 2.25 }}>
        <StatCard icon={<ViewAgendaRounded />} color="#076FF7" value={kpis.total_tokens} label="Total Tokens" />
        <StatCard icon={<CheckCircleRounded />} color="#10B981" value={kpis.completed} label="Completed" />
        <StatCard icon={<ScheduleRounded />} color="#F59E0B" value={kpis.waiting} label="Waiting" />
        <StatCard icon={<PersonRounded />} color="#7C3AED" value={kpis.in_progress} label="In Progress" />
        <StatCard icon={<TimerRounded />} color="#0EA5E9" value={kpis.avg_wait} label="Avg Wait" />
      </Grid>

      <Grid cols={2} gap={2.25} sx={{ mb: 2.25, gridTemplateColumns: { xs: '1fr', md: '1fr 1.3fr' } }}>
        <SectionCard title="Queue Overview">
          {hasOverview ? (
            <>
              <DoughnutChart labels={ov.map((o) => o[0])} data={ov.map((o) => o[2])} colors={ov.map((o) => o[1])} />
              <ChartLegend items={ov.map((o) => ({ label: `${o[0]} (${o[2]})`, color: o[1] }))} />
            </>
          ) : <Typography sx={{ color: 'text.secondary', fontSize: 13, py: 4, textAlign: 'center' }}>No token activity yet.</Typography>}
        </SectionCard>
        <SectionCard title="Token Trend" action={<Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>Last 7 days</Typography>}>
          <LineChart labels={trend_labels} series={[{ data: trend, color: '#7C3AED', fill: true }]} />
        </SectionCard>
      </Grid>

      <Grid cols={2}>
        <Paper sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.25 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Next Appointments</Typography>
            <Button component={Link} to="/todays-queue" size="small" sx={{ color: colors.primary }}>View all</Button>
          </Box>
          <DataTable columns={apptCols} rows={next_appointments} emptyText="No upcoming tokens." />
        </Paper>

        <SectionCard title="Business">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <Avatar name={business.name} size={44} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{business.name}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>Owner: {business.owner}</Typography>
            </Box>
          </Box>
        </SectionCard>
      </Grid>
    </>
  );
};

export default ProviderDashboard;
